import { useEffect, useState } from 'react';
import { useNotificationStore } from '../store/notificationStore';

/**
 * N7 (86d3w2kf4) — a ticking instant on the SERVER's clock, for relative-time copy.
 *
 * `Date.now()` plus the skew the store measured when the last page envelope landed. Two reasons it
 * is not simply `Date.now()`:
 *
 * - **"next retry in 6m" must not skew.** That countdown targets a moment the server scheduled. On a
 *   browser running two minutes fast the unskewed version reads "any moment now" while the job is
 *   still waiting, and the user presses Retry on a posting that was about to retry itself.
 * - **`serverTimeUtc` alone is frozen.** It is a stamp from the last fetch; a popover left open would
 *   show every age stuck where it was on open. The skew is the part that survives, and `Date.now()`
 *   is what keeps moving.
 *
 * ⛔ CALL THIS ONCE PER LIST, not once per row. It owns an interval, and fifty rows would mean fifty
 * timers waking the tab every 30 seconds to recompute the same number. Both surfaces call it at the
 * list level and pass `nowMs` down as a prop — which is also what makes `NotificationRow` a pure
 * function of its props.
 */
export function useServerNow(intervalMs = 30_000): number {
  // A PRIMITIVE read through a direct selector — the React Compiler tracks it as a dependency, so a
  // fresh page envelope re-renders the ages. A getter here would freeze them (86d3d1z40).
  const skewMs = useNotificationStore((s) => s.serverSkewMs);
  const [clientMs, setClientMs] = useState(() => Date.now());

  useEffect(() => {
    // 30s is the coarsest tick that still keeps every label the copy can produce honest: the finest
    // grain rendered is whole minutes ("6m"), so a faster tick would repaint identical text.
    const id = setInterval(() => setClientMs(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  return clientMs + skewMs;
}
